<template>
    <div class="transcription-summary-rater mt-4">
        <div id="summary-rating-buttons" class="rating-buttons flex items-center">
            <span class="mr-2">{{ trans('modules.Ai.rate_summary') }}:</span>
            <nord-button 
                type="button"
                @click.prevent="ratePositive" 
                class="thumbs-btn mr-2"
                :variant="rating === SummaryFeedbackRating.Positive ? 'primary' : 'plain'"
                size="s"
                aria-controls="feedback-popout"
                aria-haspopup="true"
            >
                <i class="far fa-thumbs-up"></i>
            </nord-button>
            <nord-button 
                type="button"
                @click.prevent="rateNegative" 
                class="thumbs-btn"
                :variant="rating === SummaryFeedbackRating.Negative ? 'danger' : 'plain'"
                size="s"
                aria-controls="feedback-popout"
                aria-haspopup="true"
            >
                <i class="far fa-thumbs-down"></i>
            </nord-button>
        </div>

        <!-- Popout for feedback -->
        <nord-popout 
            id="feedback-popout"
            :open="showFeedbackDrawer" 
            @close="cancelFeedback"
            position="block-end"
            anchor="summary-rating-buttons"
        >
            <div class="p-6">
                <h4 class="mb-3">{{ trans('modules.Ai.additional_feedback') }}</h4>
                <p class="mb-4">{{ trans('modules.Ai.help_improve_ai') }}</p>
                
                <nord-textarea 
                    ref="feedbackTextarea"
                    v-model="feedbackText"
                    expand="true"
                    class="mb-6"
                    rows="4"
                    :placeholder="trans('modules.Ai.feedback_placeholder')"
                ></nord-textarea>
                
                <div class="flex justify-end">
                    <nord-button 
                        type="button"
                        @click="cancelFeedback" 
                        variant="plain"
                        class="mr-3"
                    >
                        {{ trans('global.cancel') }}
                    </nord-button>
                    <nord-button 
                        type="button"
                        @click="submitFeedback(false)" 
                        variant="primary"
                        :loading="submittingFeedback"
                    >
                        {{ trans('modules.Ai.submit_feedback') }}
                    </nord-button>
                </div>
            </div>
        </nord-popout>
    </div>
</template>

<script>
import { http } from '@/system/http';
import { SummaryFeedbackRating } from './constants';

export default {
    name: 'TranscriptionSummaryRater',

    props: {
        summary: {
            type: Object,
            required: true
        },
    },

    data() {
        return {
            SummaryFeedbackRating,
            rating: null, // SummaryFeedbackRating.Positive or SummaryFeedbackRating.Negative
            showFeedbackDrawer: false,
            feedbackText: '',
            submittingFeedback: false,
            feedback_id: null,
        };
    },

    watch: {
        showFeedbackDrawer(newVal) {
            if (newVal) {
                // Focus the textarea after popout is visible
                this.$nextTick(() => {
                    setTimeout(() => {
                        if (this.$refs.feedbackTextarea && 
                            this.$refs.feedbackTextarea.focus) {
                            this.$refs.feedbackTextarea.focus();
                        }
                    }, 100); // Small delay to ensure popout has rendered
                });
            }
        },

        summary: {
            handler(newVal, oldVal) {
                if (newVal.uuid !== oldVal.uuid) {
                    this.feedback_id = null;
                    this.feedbackText = '';
                    this.submittingFeedback = false;
                    this.rating = null;
                }
            },
        },
    },

    methods: {
        ratePositive() {
            this.rating = SummaryFeedbackRating.Positive;
            this.openFeedbackPanel();
        },

        rateNegative() {
            this.rating = SummaryFeedbackRating.Negative;
            this.openFeedbackPanel();
        },

        openFeedbackPanel() {
            this.showFeedbackDrawer = true;
        },

        cancelFeedback() {
            this.showFeedbackDrawer = false;
            // Keep the rating so the button stays highlighted
        },

        async submitFeedback(silently = false) {
            this.submittingFeedback = true;

            try {
                let response;

                if (this.feedback_id) {
                    response = await http.put(`/app/ai/summary-feedback/${this.feedback_id}`, {
                        rating: this.rating,
                        feedback: this.feedbackText,
                    });
                } else {
                    response = await http.post('/app/ai/summary-feedback', {
                        rating: this.rating,
                        feedback: this.feedbackText,
                        summary_uuid: this.summary.uuid,
                    });
                    this.feedback_id = response.data.data.feedback_id;
                }

                this.submittingFeedback = false;

                if (! silently) {
                    this.showFeedbackDrawer = false;
                    toastr.success(trans('modules.Ai.feedback_thanks'));
                }

                // Emit event to parent component
                this.$emit('feedback-submitted', {
                    rating: this.rating,
                    feedback: this.feedbackText,
                    feedback_id: this.feedback_id,
                });
            } catch (error) {
                console.error('Error submitting feedback:', error);
                this.submittingFeedback = false;
                toastr.error(trans('modules.Ai.feedback_error'));
            }
        }
    },

    watch: {
        rating(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.submitFeedback(true);
            }
        },
    },
};
</script>

<style scoped>
.transcription-summary-rater {
    border-top: 1px solid #eee;
    border-bottom: 1px solid #eee;
    padding-top: 1rem;
    padding-bottom: 1rem;
}

.thumbs-btn {
    font-size: 1.2rem;
    transition: all 0.2s ease;
}

.thumbs-btn:hover {
    transform: scale(1.1);
}

#feedback-popout {
    width: 400px;
    max-width: 90vw;
}

#feedback-popout::part(content) {
    width: 100%;
}

.feedback-confirmation {
    background-color: #f9f9f9;
    border-color: #e0e0e0;
}
</style> 